<template>
    <div class="list-view">
        <ResizeSettingsModal
            v-if="resizeSettingsModalIsVisible"
            :editable="editable"
            :resize-settings="resizeSettingsToEdit"
            :is-group-resize-settings="isEditingGroupResizeSettings"
            :is-single-template-resize-settings="isEditingSingleTemplateResizeSettings"
            :preview-image-url="imageToResize"
            :template-ids="templateIdsToEditResizeSettingsOf"
            @setResizeSettings="setResizeSettings"
            @close="toggleResizeSettingsModal"
        />
        <VisibleRow
            :visible="isVisible(editable, groupsAffectingEditable, null)"
            @updateSettings="onSettingsUpdate(editable, groupsAffectingEditable, null, $event)"
        >
            <LabeledValue
                :label="EditableGroupScopeLabel"
                class="list-view__item"
                icon="md-search"
                :model-value="getFileName(editable, groupsAffectingEditable, null)"
                @click="fileSelected(editable, groupsAffectingEditable, null)"
            >
                <template #icons>
                    <Icon
                        v-if="!isDefault(editable, groupsAffectingEditable, null) && isImage"
                        type="md-crop"
                        @click.stop="showResizeSettingsModal(templatesUsingGroupMediaItem, true, false)"
                    />
                    <Icon type="md-create" @click.prevent="fileSelected(editable, groupsAffectingEditable, null)" />
                </template>
            </LabeledValue>
        </VisibleRow>
        <VisibleRow
            v-for="(label, templateId) in templateLabels"
            v-show="isOpened"
            :key="getForKey(editable, templateId)"
            :visible="isVisible(editable, groupsAffectingEditable, templateId)"
            @updateSettings="onSettingsUpdate(editable, groupsAffectingEditable, templateId, $event)"
        >
            <LockableRow
                :locked="isTemplateOverwrite(editable, groupsAffectingEditable, templateId)"
                @unlock="onUnlock(editable, groupsAffectingEditable, templateId)"
            />
            <LabeledValue
                :label="label"
                class="list-view__item"
                :label-color="getLabelColor(editable, groupsAffectingEditable, templateId)"
                icon="md-search"
                :model-value="getFileName(editable, groupsAffectingEditable, templateId)"
                @click="fileSelected(editable, groupsAffectingEditable, templateId)"
            >
                <template #icons>
                    <Icon
                        v-if="!isDefault(editable, groupsAffectingEditable, templateId) && isImage"
                        type="md-crop"
                        @click.stop="
                            showResizeSettingsModal(
                                [templateId],
                                !isTemplateOverwrite(editable, groupsAffectingEditable, templateId),
                                true
                            )
                        "
                    />
                    <Icon
                        type="md-create"
                        @click.prevent="fileSelected(editable, groupsAffectingEditable, templateId)"
                    />
                </template>
            </LabeledValue>
        </VisibleRow>
    </div>
</template>

<script lang="ts">
import { defineComponent, toRef, type PropType } from "vue";
import { EditableType } from "shared-utils/enums/editables";
import type { Editable, EditableTypeType } from "shared-types";

import LabeledValue from "@/components/Campaign/LabeledValue.vue";
import LockableRow from "@/components/Campaign/LockableRow.vue";
import VisibleRow from "@/components/Campaign/VisibleRow.vue";
import ResizeSettingsModal from "@/components/ResizeSettingsModal.vue";

import { EditableGroupScopeLabel } from "@/enums/overwrites";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditableFile } from "@/composables/useEditableFile";
import { useEditable } from "@/composables/useEditable";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useEditor } from "@/composables/useEditor";

export default defineComponent({
    name: "EditableFileListView",
    components: {
        LockableRow,
        VisibleRow,
        LabeledValue,
        ResizeSettingsModal
    },
    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },
        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },
        type: {
            type: String as PropType<EditableTypeType>,
            default: EditableType.Image
        },
        isOpened: {
            type: Boolean,
            default: true
        }
    },
    setup(props, { emit }) {
        const type = toRef(props, "type");
        const editable = toRef(props, "editable");
        const groupsAffectingEditable = toRef(props, "groupsAffectingEditable");

        const { templateLabels } = useEditable({ editable });
        const { getOverwriteMediaItem, isTemplateOverwrite, removeOverwrite, getOverwrite } = useOverwrite();
        const { getForKey, getLabelColor, getEditableSettings, getOwnSettings, updateSettings } = useEditableMethods({
            emit
        });
        const { isCampaignIndesign } = useEditor();
        const {
            resizeSettingsModalIsVisible,
            toggleResizeSettingsModal,
            fileSelected,
            showResizeSettingsModal,
            imageToResize,
            resizeSettingsToEdit,
            isEditingGroupResizeSettings,
            isEditingSingleTemplateResizeSettings,
            templateIdsToEditResizeSettingsOf,
            setResizeSettings,
            templatesUsingGroupMediaItem
        } = useEditableFile({
            type,
            editable,
            groupsAffectingEditable,
            emit
        });

        return {
            templateLabels,
            getForKey,
            getLabelColor,
            getOverwriteMediaItem,
            isTemplateOverwrite,
            resizeSettingsModalIsVisible,
            toggleResizeSettingsModal,
            fileSelected,
            showResizeSettingsModal,
            imageToResize,
            resizeSettingsToEdit,
            isEditingGroupResizeSettings,
            isEditingSingleTemplateResizeSettings,
            templateIdsToEditResizeSettingsOf,
            setResizeSettings,
            templatesUsingGroupMediaItem,
            getEditableSettings,
            getOwnSettings,
            isCampaignIndesign,
            removeOverwrite,
            getOverwrite,
            updateSettings
        };
    },
    data() {
        return {
            EditableGroupScopeLabel
        };
    },
    computed: {
        isImage() {
            return this.type === EditableType.Image;
        }
    },

    methods: {
        getFileName(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            // todo decide on how to obtain default value
            const mediaItem = this.getOverwriteMediaItem(editable, groupsAffectingEditable, templateId);

            return mediaItem ? mediaItem.name : "Default";
        },

        isDefault(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            return !this.getOverwriteMediaItem(editable, groupsAffectingEditable, templateId);
        },

        isVisible(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            const settings = this.getEditableSettings(editable, groupsAffectingEditable, templateId);

            if (!settings) {
                return true;
            }

            return settings.visible !== false;
        },

        onUnlock(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            const overwrite = this.getOverwrite(editable, groupsAffectingEditable, templateId);
            this.removeOverwrite(overwrite._id);
        },

        onSettingsUpdate(
            editable: Editable,
            groupsAffectingEditable: string[],
            templateId: string | null,
            payload: { visible: boolean }
        ) {
            // An InDesign size inherits per key, so start from what this row sets, not what it resolves to
            const settings = this.isCampaignIndesign
                ? this.getOwnSettings(editable, groupsAffectingEditable, templateId)
                : this.getEditableSettings(editable, groupsAffectingEditable, templateId);
            const mergedSettings = {
                ...settings,
                ...payload
            };

            this.updateSettings({
                editable,
                settings: mergedSettings,
                groupsAffectingEditable,
                templateId
            });
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.list-view {
    background-color: $cmp-dark-secondary-sidebar-color;
    font-size: 12px;
    display: flex !important;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    flex: 1 0 100%;

    &.labeled-value {
        height: 34px;
    }

    &__item {
        line-height: 34px;
        padding-left: 10px;
        height: 34px;
    }
}
</style>
